import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

export default function AdminLogin() {
  const [form, setForm]               = useState({ email: '', password: '' });
  const [error, setError]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile]       = useState(window.innerWidth < 640);
  const navigate = useNavigate();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/admin/login', form);
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminInfo', JSON.stringify(data.admin));
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={st.container}>
      <div style={{ ...st.card, flexDirection: isMobile ? 'column' : 'row' }}>

        {/* ── Left Panel ── */}
        {!isMobile && (
          <div style={st.leftPanel}>
            <div style={st.leftGlow} />
            <div style={st.leftContent}>
              {/* Logo */}
              <div style={st.logoWrap}>
                <img
                  src="/app-logo.svg"
                  alt="Shield Secure"
                  style={st.logoImg}
                />
              </div>
              <div style={st.brandName}>Shield Secure</div>
              <h1 style={st.welcomeText}>Hi there,<br />welcome in!</h1>
              <p style={st.welcomeSub}>Secure. Monitor. Protect.</p>
            </div>
            {/* Skyline */}
            <div style={st.skyline}>
              {[
                { h: 110, w: 22, l: 0   },
                { h: 150, w: 28, l: 26  },
                { h: 90,  w: 18, l: 58  },
                { h: 180, w: 32, l: 80  },
                { h: 120, w: 24, l: 116 },
                { h: 200, w: 36, l: 144 },
                { h: 130, w: 26, l: 184 },
                { h: 160, w: 30, l: 214 },
                { h: 100, w: 20, l: 248 },
              ].map((b, i) => (
                <div key={i} style={{
                  position: 'absolute', bottom: 0, left: b.l,
                  width: b.w, height: b.h,
                  background: `rgba(99,102,241,${0.12 + (i % 3) * 0.1})`,
                  borderRadius: '3px 3px 0 0',
                }} />
              ))}
            </div>
          </div>
        )}

        {/* ── Right Panel ── */}
        <div style={{ ...st.rightPanel, padding: isMobile ? '32px 24px' : '48px 40px' }}>

          {/* Mobile: show logo at top */}
          {isMobile && (
            <div style={st.mobileHeader}>
              <img src="/app-logo.svg" alt="Shield Secure" style={st.mobileLogoImg} />
              <span style={st.mobileBrandName}>Shield Secure</span>
            </div>
          )}

          <div style={st.formWrap}>
            <div style={st.titleBadge}>Shield Secure Admin</div>
            <h3 style={{ ...st.heading, fontSize: isMobile ? 24 : 28 }}>Let's get you logged in</h3>
            <p style={st.subtext}>
              Enter your details below to securely access your account.
            </p>

            <form onSubmit={handleSubmit}>
              <div style={st.field}>
                <label style={st.label}>Email Address</label>
                <div style={st.inputWrap}>
                  <span style={st.inputIcon}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                      <rect x="2" y="4" width="20" height="16" rx="2"/>
                      <path d="M2 7l10 7 10-7"/>
                    </svg>
                  </span>
                  <input
                    style={st.input}
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div style={st.field}>
                <label style={st.label}>Password</label>
                <div style={st.inputWrap}>
                  <span style={st.inputIcon}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </span>
                  <input
                    style={st.input}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                  />
                  <span style={st.eyeIcon} onClick={() => setShowPassword(s => !s)}>
                    {showPassword ? (
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    ) : (
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    )}
                  </span>
                </div>
              </div>

              {error && <p style={st.error}>{error}</p>}

              <button
                style={{ ...st.button, opacity: loading ? 0.75 : 1 }}
                type="submit"
                disabled={loading}
              >
                {loading ? 'Logging in…' : 'Log In →'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

const st = {
  container: {
    minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg, #0d0f1a 0%, #3e4bad 50%, #2f3558 100%)',
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    padding: '16px',
  },
  card: {
    display: 'flex',
    width: '100%', maxWidth: '820px',
    borderRadius: 18, overflow: 'hidden',
    boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
    border: '1px solid rgba(99,102,241,0.15)',
  },

  /* Left */
  leftPanel: {
    width: '280px', flexShrink: 0,
    background: 'linear-gradient(160deg, #1e1b4b 0%, #1a1f3a 55%, #0f172a 100%)',
    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
    position: 'relative', overflow: 'hidden',
  },
  leftGlow: {
    position: 'absolute', top: -80, right: -60,
    width: 260, height: 260, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  leftContent: { padding: '40px 28px 0', position: 'relative', zIndex: 1 },
  logoWrap: {
    width: 64, height: 64, borderRadius: 16,
    background: 'rgba(99,102,241,0.15)',
    border: '1px solid rgba(99,102,241,0.3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: 20,
    overflow: 'hidden',
  },
  logoImg: { width: 48, height: 48, objectFit: 'contain' },
  brandName: {
    fontSize: 12, fontWeight: 700, color: '#429e66',
    textTransform: 'uppercase', letterSpacing: 2, marginBottom: 16,
  },
  welcomeText: { fontSize: 26, fontWeight: 800, color: '#f1f5f9', lineHeight: 1.3, margin: '0 0 10px' },
  welcomeSub:  { fontSize: 13, color: '#64748b', margin: 0 },
  skyline: { position: 'relative', height: 180, width: '100%' },

  /* Right */
  rightPanel: {
    flex: 1, background: '#181c2e',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  formWrap: { width: '100%', maxWidth: 360 },

  /* Mobile header */
  mobileHeader: {
    display: 'flex', alignItems: 'center', gap: 12,
    marginBottom: 28,
    paddingBottom: 20,
    borderBottom: '1px solid rgba(255,255,255,0.07)',
  },
  mobileLogoImg: { width: 40, height: 40, objectFit: 'contain', borderRadius: 10 },
  mobileBrandName: { fontSize: 16, fontWeight: 700, color: '#f1f5f9' },

  titleBadge: {
    display: 'inline-block',
    fontSize: 11, fontWeight: 700, color: '#429e66',
    textTransform: 'uppercase', letterSpacing: 1.5,
    background: 'rgba(99, 241, 149, 0.1)',
    border: '1px solid rgba(99,102,241,0.25)',
    borderRadius: 20, padding: '4px 12px',
    marginBottom: 14,
  },
  heading: { fontWeight: 800, color: '#f1f5f9', margin: '0 0 8px' },
  subtext:  { fontSize: 13, color: '#64748b', marginBottom: 28, lineHeight: 1.6 },

  field: { marginBottom: 18 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#cbd5e1', marginBottom: 7 },
  inputWrap: {
    display: 'flex', alignItems: 'center',
    border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: 10,
    background: '#1e2340', transition: 'border-color 0.2s',
  },
  inputIcon: { padding: '0 12px', display: 'flex', alignItems: 'center', flexShrink: 0 },
  eyeIcon:   { padding: '0 12px', display: 'flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 },
  input: {
    flex: 1, padding: '13px 0', border: 'none', outline: 'none',
    fontSize: 14, color: '#f1f5f9', background: 'transparent',
  },
  error:  { color: '#f87171', fontSize: 13, marginBottom: 12 },
  button: {
    width: '100%', padding: '14px',
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    color: '#fff', border: 'none', borderRadius: 10,
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
    marginTop: 6, letterSpacing: 0.3,
    boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
    transition: 'all 0.2s ease',
  },
};
